import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Home,
  Users,
  DollarSign,
  FileText,
  Package,
  AlertTriangle,
  Clock,
  PieChart,
  PackagePlus,
  ArrowDown,
  Zap,
  Menu,
  X,
  MessageSquare,
  Receipt
} from 'lucide-react';
import { BiAnalyse } from 'react-icons/bi';

function PlutoMenu({ rank, user_id }) {
  const { referral_id, page, second_page, third_page } = useParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState !== null) {
      setIsCollapsed(JSON.parse(savedState));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [page, second_page, third_page]);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActive = (menuPage) => {
    return page === 'pluto' && second_page === menuPage;
  };

  const menuItems = [
    {
      label: 'Dashboard',
      path: '/pluto',
      icon: <Home size={18} />,
      access: true,
      isActive: second_page === undefined,
      category: 'main'
    },
    {
      label: 'Users',
      path: '/pluto/user',
      icon: <Users size={18} />,
      access: rank !== 4 && rank !== 7,
      isActive: isActive('user'),
      category: 'management'
    },
    {
      label: 'Withdrawals',
      path: '/pluto/withdrawals',
      icon: <DollarSign size={18} />,
      access: rank === 10,
      isActive: isActive('withdrawals'),
      category: 'financial'
    },
    {
      label: 'Invoices',
      path: '/pluto/invoices',
      icon: <FileText size={18} />,
      access: rank === 10,
      isActive: isActive('invoices'),
      category: 'financial'
    },
    {
      label: 'Accounting',
      path: '/pluto/accounting-transactions',
      icon: <Receipt size={18} />,
      access: rank === 10,
      isActive: isActive('accounting-transactions'),
      category: 'financial'
    },
    {
      label: 'PTCS',
      path: '/pluto/ptcs',
      icon: <Package size={18} />,
      access: rank === 10,
      isActive: isActive('ptcs'),
      category: 'financial'
    },
    {
      label: 'Chargebacks',
      path: '/pluto/chargebacks',
      icon: <AlertTriangle size={18} />,
      access: rank >= 8,
      isActive: isActive('chargebacks'),
      category: 'financial'
    },
    {
      label: 'Pending Offers',
      path: '/pluto/pending-offers',
      icon: <Clock size={18} />,
      access: rank >= 8,
      isActive: isActive('pending-offers'),
      category: 'offers'
    },
    {
      label: 'Suggested Pending',
      path: '/pluto/suggested-pending-offers',
      icon: <AlertTriangle size={18} />,
      access: rank >= 8,
      isActive: isActive('suggested-pending-offers'),
      category: 'offers'
    },
    {
      label: 'Suspect Offers',
      path: '/pluto/suspect-offers',
      icon: <AlertTriangle size={18} />,
      access: rank >= 8,
      isActive: isActive('suspect-offers'),
      category: 'offers'
    },
    {
      label: 'OfferWalls',
      path: '/pluto/offerwalls',
      icon: <Package size={18} />,
      access: rank === 10,
      isActive: isActive('offerwalls'),
      category: 'offers'
    },
    {
      label: 'PaidCash Offers',
      path: '/pluto/pc-offers',
      icon: <DollarSign size={18} />,
      access: rank === 10,
      isActive: isActive('pc-offers'),
      category: 'offers'
    },
    {
      label: 'Offers',
      path: '/pluto/offers',
      icon: <Package size={18} />,
      access: rank >= 8,
      isActive: isActive('offers'),
      category: 'offers'
    },
    {
      label: 'Offer Analytics',
      path: '/pluto/offer-analytics',
      icon: <BiAnalyse size={18} />,
      access: rank >= 8,
      isActive: isActive('offer-analytics'),
      category: 'analytics'
    },
    {
      label: 'Offers Data',
      path: '/pluto/offer-total',
      icon: <PieChart size={18} />,
      access: rank >= 8,
      isActive: isActive('offer-total'),
      category: 'analytics'
    },
    {
      label: 'Offers Data Today',
      path: '/pluto/offer-today',
      icon: <PieChart size={18} />,
      access: rank >= 8,
      isActive: isActive('offer-today'),
      category: 'analytics'
    },
    {
      label: 'New Offers',
      path: '/pluto/offers-today',
      icon: <PackagePlus size={18} />,
      access: rank >= 8,
      isActive: isActive('offers-today'),
      category: 'analytics'
    },
    {
      label: 'Withdrawals Today',
      path: '/pluto/withdrawals-today',
      icon: <ArrowDown size={18} />,
      access: rank >= 8,
      isActive: isActive('withdrawals-today'),
      category: 'analytics'
    },
    {
      label: 'Fast Completion',
      path: '/pluto/offer-fastc',
      icon: <Zap size={18} />,
      access: rank >= 8,
      isActive: isActive('offer-fastc'),
      category: 'analytics'
    },
    {
      label: 'Support Chat',
      path: '/pluto/support-chat',
      icon: <MessageSquare size={18} />,
      access: rank >= 8,
      isActive: isActive('support-chat'),
      category: 'management'
    }
  ];

  const accessibleMenuItems = menuItems.filter(item => item.access);
  const activeItem = accessibleMenuItems.find(item => item.isActive);

  const groupedMenuItems = accessibleMenuItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  const categoryLabels = {
    main: 'Main',
    management: 'Management',
    financial: 'Financial',
    offers: 'Offers',
    analytics: 'Analytics'
  };

  const MenuItem = ({ item, isMobile = false, onClick }) => (
    <Link
      to={item.path}
      className={`pluto-sidebar-item ${item.isActive ? 'active' : ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '12px 16px' : (isCollapsed ? '10px' : '10px 16px'),
        margin: isMobile ? '1px 0' : '2px 8px',
        borderRadius: '8px',
        color: item.isActive ? '#58a6ff' : '#8b949e',
        fontSize: '14px',
        fontWeight: item.isActive ? '600' : '500',
        transition: 'all 0.2s',
        textDecoration: 'none',
        background: item.isActive ? 'rgba(56, 139, 253, 0.1)' : 'transparent',
        borderLeft: item.isActive ? '2px solid #58a6ff' : '2px solid transparent',
        justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
        position: 'relative'
      }}
      onClick={onClick}
      title={isCollapsed && !isMobile ? item.label : ''}
    >
      <span style={{
        marginRight: (!isCollapsed || isMobile) ? '12px' : '0',
        flexShrink: 0,
        color: item.isActive ? '#58a6ff' : '#8b949e',
        display: 'flex',
        alignItems: 'center'
      }}>
        {item.icon}
      </span>

      {(!isCollapsed || isMobile) && (
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
      )}

      {isCollapsed && !isMobile && (
        <div style={{
          position: 'absolute',
          left: '100%',
          marginLeft: '8px',
          padding: '6px 12px',
          background: '#21262d',
          color: '#e6edf3',
          fontSize: '13px',
          borderRadius: '6px',
          opacity: 0,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          zIndex: 50,
          border: '1px solid #30363d',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          transition: 'opacity 0.15s'
        }} className="group-hover:opacity-100">
          {item.label}
        </div>
      )}
    </Link>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden">
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          background: '#0d1117',
          borderBottom: '1px solid #21262d'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px' }}>
            <div onClick={toggleMobileMenu} style={{ cursor: 'pointer', color: '#8b949e', display: 'flex', alignItems: 'center' }}>
              <Menu size={24} />
            </div>

            {activeItem && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#161b22',
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid #21262d'
              }}>
                <span style={{ color: '#58a6ff' }}>{activeItem.icon}</span>
                <span style={{ fontSize: '14px', color: '#e6edf3', fontWeight: '500' }}>
                  {activeItem.label}
                </span>
              </div>
            )}
          </div>
        </div>

        <div style={{ height: '56px' }}></div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <>
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.5)' }}
              onClick={toggleMobileMenu}
            ></div>
            <nav style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              zIndex: 50,
              display: 'flex',
              flexDirection: 'column',
              width: '280px',
              maxWidth: '85vw',
              background: '#0d1117',
              boxShadow: '4px 0 20px rgba(0,0,0,0.4)',
              borderRight: '1px solid #21262d'
            }}>
              {/* Mobile Menu Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                borderBottom: '1px solid #21262d'
              }}>
                <span style={{ fontWeight: '700', fontSize: '18px', color: '#e6edf3' }}>Admin Panel</span>
                <div onClick={toggleMobileMenu} style={{ cursor: 'pointer', color: '#8b949e' }}>
                  <X size={20} />
                </div>
              </div>

              {/* Mobile Menu Items */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                {Object.entries(groupedMenuItems).map(([category, items]) => (
                  <div key={category} style={{ marginBottom: '4px' }}>
                    <div className="pluto-sidebar-category">
                      {categoryLabels[category] || category}
                    </div>
                    {items.map((item, index) => (
                      <MenuItem
                        key={index}
                        item={item}
                        isMobile={true}
                        onClick={toggleMobileMenu}
                      />
                    ))}
                  </div>
                ))}
              </div>

              {/* Mobile Menu Footer */}
              <div style={{ padding: '12px 16px', borderTop: '1px solid #21262d' }}>
                <div style={{ fontSize: '12px', color: '#484f58' }}>
                  Rank: <span style={{ color: '#58a6ff', fontWeight: '500' }}>{rank}</span> | User: <span style={{ color: '#58a6ff', fontWeight: '500' }}>{user_id}</span>
                </div>
              </div>
            </nav>
          </>
        )}
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100%',
            background: '#0d1117',
            borderRight: '1px solid #21262d',
            transition: 'width 0.3s ease',
            zIndex: 30,
            width: isCollapsed ? '64px' : '256px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Desktop Header */}
          <div
            onClick={toggleSidebar}
            style={{
              display: 'flex',
              justifyContent: isCollapsed ? 'center' : 'flex-end',
              padding: '16px',
              cursor: 'pointer',
              color: '#8b949e',
              borderBottom: '1px solid #21262d'
            }}
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </div>

          {/* Desktop Menu Items */}
          <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
            {Object.entries(groupedMenuItems).map(([category, items]) => (
              <div key={category} style={{ marginBottom: '8px' }}>
                {!isCollapsed && (
                  <div className="pluto-sidebar-category">
                    {categoryLabels[category] || category}
                  </div>
                )}
                {items.map((item, index) => (
                  <MenuItem key={index} item={item} />
                ))}
                {!isCollapsed && <div style={{ height: '4px' }}></div>}
              </div>
            ))}
          </nav>

          {/* Desktop Footer */}
          {!isCollapsed && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid #21262d' }}>
              <div style={{ fontSize: '12px', color: '#484f58' }}>
                Rank: <span style={{ color: '#58a6ff', fontWeight: '500' }}>{rank}</span> | User: <span style={{ color: '#58a6ff', fontWeight: '500' }}>{user_id}</span>
              </div>
            </div>
          )}
        </div>

        {/* Main Content Spacer */}
        <div style={{ transition: 'margin-left 0.3s ease', marginLeft: isCollapsed ? '64px' : '256px' }}></div>
      </div>
    </>
  );
}

export default PlutoMenu;
